import type { AppliedRuleSummaryItem, SalaryCalcData } from '../../api/salaryCalc';
import { convertToKRW } from '../../lib/utils';
import type { SettlementType } from '../centerSettlement/types';
import { formatAppliedRuleLabel, hasExceptionRuleRows } from '../settlementClassification/appliedRule';
import type { SettlementCategory } from '../settlementClassification/types';

export type CommissionUnit = 'percentage' | 'fixed';

const asCommissionUnit = (value: unknown): CommissionUnit | null =>
    value === 'fixed' || value === 'percentage' ? value : null;

/**
 * 요율 표기 — 고정 금액(fixed)이면 회당 원(콤마), 비율이면 %, 단위를 모르면 「-」.
 * 강사 요율이 고정 금액인데 「18000%」로 보이던 표시 오류 때문에 한 곳으로 모았다. 값은 서버 그대로, 단위만 붙인다.
 * 모를 때 숫자만 두면 「18000」·「40」 중 무엇인지 읽는 사람이 단정하게 되므로 칸을 비운다
 * (세션별 구간 표의 「예외 수업료」 요율 칸과 같은 「-」 관례).
 */
export const formatCommissionRateText = (rate: number, unit: CommissionUnit | null): string =>
    unit === 'fixed' ? `${convertToKRW(rate)}원` : unit === 'percentage' ? `${rate}%` : '-';

/**
 * 이 강사 이번 달 요율 단위.
 *  1) 급여 응답의 tierCommissionType(상세 정산) / sessionCommissionType(세션별 정산) — 그 강사 그 달 계산에 서버가 실제 쓴 값(신서버).
 *  2) 상세 정산 구서버: 강사 요율표 조회(v1/coachCommissionTables/center)의 그 강사 값. 서버 급여 계산과 같은
 *     getCoachCommissionTablesByCoachIds(같은 월 상속 규칙)로 만든다. 조회 중·실패·그 강사 없음이면 null(단정하지 않는다).
 *  3) 세션별 정산 구서버: 강사별 방식을 읽을 조회가 없다(센터 조회는 첫 강사의 방식 하나만 준다 — 강사마다 다를 수 있다).
 *     그래서 이 기능 전 표기(%) 그대로 둔다. 신서버 배포 뒤에는 1)이 이긴다.
 */
export const resolveCommissionUnit = ({
    settlementType,
    data,
    tierTableCommissionType,
}: {
    settlementType: SettlementType | string;
    data: Partial<SalaryCalcData> | undefined | null;
    /** 강사 요율표 조회에서 그 강사의 commissionType. 조회 중·실패·없음이면 undefined */
    tierTableCommissionType?: unknown;
}): CommissionUnit | null => {
    if (settlementType === 'tier') {
        return asCommissionUnit(data?.tierCommissionType) ?? asCommissionUnit(tierTableCommissionType);
    }
    if (settlementType === 'sessionTier') {
        return asCommissionUnit(data?.sessionCommissionType) ?? 'percentage';
    }
    return null;
};

const isNum = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * 근무 시간 내/외(IN/OUT) 줄에 보일 건수·금액 — 서버 값만 고른다(화면에서 더하거나 빼지 않는다).
 *
 * 신서버(상세 정산)는 구간표로만 계산한 수업(appliedRule === 'tier')의 IN/OUT 을 따로 준다
 * (inTierScheduleCount·inTierScheduleFee·outTierScheduleCount·outTierScheduleFee).
 * 넷 다 숫자이고 적용 규칙별 소계(appliedRuleSummary)도 함께 왔을 때만 그 값을 쓴다 —
 * 예외·개별 요율 수업은 IN/OUT 에서 빠지는 대신 소계에 보여야 하므로, 소계가 없으면 빠진 수업이 화면에서 사라진다.
 * 그 외(구서버·기본 정산)는 지금 그대로 in/outScheduleCount·totalIn/OutScheduleFee.
 */
export type InOutDisplay = {
    isTierSplit: boolean;
    inCount: number;
    inFee: number;
    outCount: number;
    outFee: number;
};

export const resolveInOutDisplay = (
    data: Partial<SalaryCalcData> | undefined | null,
    settlementType: SettlementType | string,
): InOutDisplay => {
    const split =
        settlementType === 'tier' &&
        Array.isArray(data?.appliedRuleSummary) &&
        isNum(data?.inTierScheduleCount) &&
        isNum(data?.inTierScheduleFee) &&
        isNum(data?.outTierScheduleCount) &&
        isNum(data?.outTierScheduleFee);
    if (split && data) {
        return {
            isTierSplit: true,
            inCount: data.inTierScheduleCount as number,
            inFee: data.inTierScheduleFee as number,
            outCount: data.outTierScheduleCount as number,
            outFee: data.outTierScheduleFee as number,
        };
    }
    return {
        isTierSplit: false,
        inCount: data?.inScheduleCount ?? 0,
        inFee: data?.totalInScheduleFee ?? 0,
        outCount: data?.outScheduleCount ?? 0,
        outFee: data?.totalOutScheduleFee ?? 0,
    };
};

/**
 * 적용 규칙별 소계에서 화면에 보일 행과 제목.
 *  - IN/OUT 이 구간표 수업만 보일 때(isTierSplit): 「기본」(tier) 행은 IN/OUT 과 같은 수업이라 빼고,
 *    예외·개별 요율 행만 보인다. 그래야 IN + OUT + 아래 행 = PT 수업료로 읽힌다.
 *    개별 요율만 있는 센터도 그 수업이 IN/OUT 에서 빠졌으므로 소계를 보인다.
 *  - 아니면 지금 그대로: 분류·재등록 예외가 있을 때만 전체 행(기본 포함).
 */
export const resolveAppliedRuleSummaryDisplay = (
    rows: AppliedRuleSummaryItem[],
    isTierSplit: boolean,
): { show: boolean; title: string; rows: AppliedRuleSummaryItem[] } => {
    if (isTierSplit) {
        const nonTier = rows.filter((row) => row.appliedRule !== 'tier');
        return { show: nonTier.length > 0, title: '예외·개별 요율 PT 수업료', rows: nonTier };
    }
    return { show: hasExceptionRuleRows(rows), title: '적용 규칙별 PT 수업료', rows };
};

const countOf = (value: unknown): number => (isNum(value) && value > 0 ? value : 0);

/**
 * 「시간 상관없이 IN」 표시 — 서버 건수를 고르기만 한다(더하거나 빼서 새 숫자를 만들지 않는다. 0원 경고만 PT+그룹 두 서버 값의 합).
 *
 *  - inNoshowCount: IN 줄에 들어간, 근무 시간 밖 노쇼 건수.
 *      IN 줄이 구간표 수업만 보일 때(isTierSplit)는 noshowForcedInTierScheduleCount(= 그 줄의 일부),
 *      아니면 IN 줄이 모든 수업이라 noshowForcedInScheduleCount.
 *  - inRuleCount: IN 줄에 들어간 「항상 IN 요율의 배수」 예외 건수 — isTierSplit 이면 예외 소계로 가므로 0.
 *  - groupNoshowCount: 그룹 수업 줄에 들어간 근무 시간 밖 노쇼 예약 수.
 *  - zeroRateCount: 시간과 상관없이 IN 으로 계산했는데 IN 요율이 0이라 0원이 된 수업(PT + 그룹).
 * 기능을 안 쓰면 서버가 키를 싣지 않아 전부 0 — 화면은 지금 그대로.
 */
export type ForcedInNotes = {
    inNoshowCount: number;
    inRuleCount: number;
    groupNoshowCount: number;
    zeroRateCount: number;
};

export const resolveForcedInNotes = (
    data: Partial<SalaryCalcData> | undefined | null,
    settlementType: SettlementType | string,
    isTierSplit: boolean,
): ForcedInNotes => {
    if (settlementType !== 'tier' || !data) {
        return { inNoshowCount: 0, inRuleCount: 0, groupNoshowCount: 0, zeroRateCount: 0 };
    }
    return {
        inNoshowCount: isTierSplit
            ? countOf(data.noshowForcedInTierScheduleCount)
            : countOf(data.noshowForcedInScheduleCount),
        inRuleCount: isTierSplit ? 0 : countOf(data.ruleForcedInScheduleCount),
        groupNoshowCount: countOf(data.groupNoshowForcedInScheduleCount),
        zeroRateCount: countOf(data.forcedInZeroRateScheduleCount) + countOf(data.groupForcedInZeroRateScheduleCount),
    };
};

/** IN 줄 제목 — 시간 밖인데 IN 으로 넣은 수업이 있으면 「근무 시간 내」만으로는 틀리므로 바꾼다. 없으면 지금 그대로. */
export const inLineTitle = (notes: ForcedInNotes): string =>
    notes.inNoshowCount > 0 || notes.inRuleCount > 0 ? '근무 시간 내 수업 + 항상 IN' : '근무 시간 내 수업';

/** IN 줄 아래 주석. 없으면 null. 예: 「노쇼 3건 포함」, 「노쇼 3건 · 예외 2건 포함」 */
export const inLineNote = (notes: ForcedInNotes): string | null => {
    const parts = [
        ...(notes.inNoshowCount > 0 ? [`노쇼 ${notes.inNoshowCount}건`] : []),
        ...(notes.inRuleCount > 0 ? [`예외 ${notes.inRuleCount}건`] : []),
    ];
    return parts.length > 0 ? `근무 시간 밖 ${parts.join(' · ')} 포함` : null;
};

/** 적용 규칙별 소계 행 이름 — 「항상 IN 요율의 배수」 행은 「바디챌린지 · IN 요율 ×1.2」. */
export const formatAppliedRuleSummaryLabel = (
    item: AppliedRuleSummaryItem,
    categoryMap: Map<string, SettlementCategory>,
): string | null =>
    formatAppliedRuleLabel(
        item.appliedRule,
        categoryMap,
        item.exceptionMode === 'inMultiplier' ? item.exceptionValue : undefined,
        item.exceptionMode,
    );
